import React, { useState, useEffect } from 'react';
import { toast } from 'sonner';
import { FileText, Upload, Mail, RefreshCw, Search, Check, X, Calendar, User, Filter, Download, ExternalLink } from 'lucide-react';
import Sidebar from '../components/Sidebar';
import FilterSelect from '../components/FilterSelect';
import FilterInput from '../components/FilterInput';
import api from '../services/api';
import { useTheme } from '../contexts/ThemeContext';
import { resolveFileUrl } from '@/lib/utils';

const BRAND_BLUE = '#2B4B6A';
const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Tab Component
const Tab = ({ active, onClick, children, icon: Icon, count }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-5 py-3 font-medium transition-all border-b-2 ${
      active 
        ? 'border-[#2B4B6A] text-[#2B4B6A]' 
        : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
    }`}
  >
    <Icon className="w-4 h-4" />
    {children}
    {count !== undefined && (
      <span className={`px-2 py-0.5 text-xs rounded-full ${active ? 'bg-[#2B4B6A] text-white' : 'bg-gray-200 text-gray-600'}`}>
        {count}
      </span>
    )}
  </button>
);

// Stat Card
const StatCard = ({ label, value, icon: Icon, color = 'blue' }) => {
  const colorClasses = {
    blue: 'bg-blue-50 text-blue-600',
    green: 'bg-green-50 text-green-600',
    red: 'bg-red-50 text-red-600',
    yellow: 'bg-yellow-50 text-yellow-600'
  };

  return (
    <div className="bg-white rounded-xl p-4 border border-gray-200">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{label}</p>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
        </div>
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
};

// Status Badge
const StatusBadge = ({ success }) => (
  <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${
    success ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
  }`}>
    {success ? <Check className="w-3 h-3" /> : <X className="w-3 h-3" />}
    {success ? 'Success' : 'Failed'}
  </span>
);

// Upload Logs Tab
const UploadLogsTab = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    success: '',
    action: ''
  });
  const [actions, setActions] = useState([]);

  useEffect(() => {
    fetchLogs();
    fetchActions();
  }, [page, filters]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: 30 });
      if (filters.success !== '') params.append('success', filters.success);
      if (filters.action) params.append('action', filters.action);
      
      const res = await api.get(`/admin/logs/uploads?${params}`);
      setLogs(res.data.logs);
      setStats(res.data.stats);
      setTotalPages(res.data.pages);
    } catch (error) {
      toast.error('Failed to load upload logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchActions = async () => {
    try {
      const res = await api.get('/admin/logs/uploads/actions');
      setActions(res.data.actions || []);
    } catch (error) {
      console.error('Failed to fetch actions');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  const formatSize = (bytes) => {
    if (!bytes) return '-';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard label="Total Uploads" value={stats.total_uploads || 0} icon={Upload} color="blue" />
        <StatCard label="Successful" value={stats.successful || 0} icon={Check} color="green" />
        <StatCard label="Failed" value={stats.failed || 0} icon={X} color="red" />
        <StatCard label="Today" value={stats.today || 0} icon={Calendar} color="yellow" />
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm text-gray-600">Filters:</span>
        </div>
        <FilterSelect
          value={filters.success}
          onChange={(e) => { setFilters({...filters, success: e.target.value}); setPage(1); }}
          options={[
            { value: '', label: 'All Status' },
            { value: 'true', label: 'Success' },
            { value: 'false', label: 'Failed' }
          ]}
        />
        <FilterSelect
          value={filters.action}
          onChange={(e) => { setFilters({...filters, action: e.target.value}); setPage(1); }}
          options={[
            { value: '', label: 'All Actions' },
            ...actions.map(action => ({ value: action, label: action.replace(/_/g, ' ') }))
          ]}
        />
        <button
          onClick={fetchLogs}
          className="ml-auto flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 mx-auto" style={{ borderTopColor: BRAND_BLUE }}></div>
          </div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No upload logs found
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Time</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Action</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">File</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Size</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">User</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(log.timestamp)}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 bg-gray-100 text-gray-700 rounded text-xs font-medium">
                      {log.action?.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm max-w-[200px]">
                    {log.file_url && log.success ? (
                      <a
                        href={resolveFileUrl(log.file_url, API_URL)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 hover:underline flex items-center gap-1 truncate"
                        title={log.filename}
                      >
                        <span className="truncate">{log.filename || '-'}</span>
                        <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      </a>
                    ) : (
                      <span className="text-gray-900 truncate block" title={log.filename}>
                        {log.filename || '-'}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{formatSize(log.file_size)}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {log.user_email || log.user_id?.slice(0, 8) || '-'}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge success={log.success} />
                    {log.error_message && (
                      <div className="text-xs text-red-600 mt-1 max-w-[200px] truncate" title={log.error_message}>
                        {log.error_message}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>
          <span className="px-3 py-1.5 text-sm text-gray-600">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

// Email Logs Tab
const EmailLogsTab = () => {
  const [logs, setLogs] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [filters, setFilters] = useState({
    status: '',
    email_type: '',
    recipient: ''
  });
  const [emailTypes, setEmailTypes] = useState([]);

  useEffect(() => {
    fetchLogs();
    fetchTypes();
  }, [page, filters]);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page, limit: 30 });
      if (filters.status) params.append('status', filters.status);
      if (filters.email_type) params.append('email_type', filters.email_type);
      if (filters.recipient) params.append('recipient', filters.recipient);
      
      const res = await api.get(`/admin/logs/emails?${params}`);
      setLogs(res.data.logs);
      setStats(res.data.stats);
      setTotalPages(res.data.pages);
    } catch (error) {
      toast.error('Failed to load email logs');
    } finally {
      setLoading(false);
    }
  };

  const fetchTypes = async () => {
    try {
      const res = await api.get('/admin/logs/emails/types');
      setEmailTypes(res.data.types || []);
    } catch (error) {
      console.error('Failed to fetch email types');
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleString();
  };

  return (
    <div className="space-y-6">
      {/* Stats */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard label="Total Emails" value={stats.total_emails || 0} icon={Mail} color="blue" />
        <StatCard label="Sent" value={stats.sent || 0} icon={Check} color="green" />
        <StatCard label="Failed" value={stats.failed || 0} icon={X} color="red" />
        <StatCard label="Today" value={stats.today || 0} icon={Calendar} color="yellow" />
      </div>

      {/* Filters */}
      <div className="flex gap-4 items-center">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-gray-500" />
          <span className="text-sm text-gray-600">Filters:</span>
        </div>
        <FilterSelect
          value={filters.status}
          onChange={(e) => { setFilters({...filters, status: e.target.value}); setPage(1); }}
          options={[
            { value: '', label: 'All Status' },
            { value: 'sent', label: 'Sent' },
            { value: 'failed', label: 'Failed' }
          ]}
        />
        <FilterSelect
          value={filters.email_type}
          onChange={(e) => { setFilters({...filters, email_type: e.target.value}); setPage(1); }}
          options={[
            { value: '', label: 'All Types' },
            ...emailTypes.map(type => ({ value: type, label: type.replace(/_/g, ' ') }))
          ]}
        />
        <FilterInput
          value={filters.recipient}
          onChange={(e) => { setFilters({...filters, recipient: e.target.value}); setPage(1); }}
          placeholder="Search recipient..."
          className="w-48"
        />
        <button
          onClick={fetchLogs}
          className="ml-auto flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-600 hover:text-gray-900 border border-gray-200 rounded-lg hover:bg-gray-50"
        >
          <RefreshCw className="w-4 h-4" />
          Refresh
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        {loading ? (
          <div className="p-8 text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-2 border-gray-300 mx-auto" style={{ borderTopColor: BRAND_BLUE }}></div>
          </div>
        ) : logs.length === 0 ? (
          <div className="p-8 text-center text-gray-500">
            No email logs found
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Time</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Type</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Recipient</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Subject</th>
                <th className="text-left px-4 py-3 text-xs font-semibold text-gray-600 uppercase">Status</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {logs.map((log) => (
                <tr key={log.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm text-gray-600">{formatDate(log.sent_at)}</td>
                  <td className="px-4 py-3">
                    <span className="px-2 py-1 bg-blue-50 text-blue-700 rounded text-xs font-medium">
                      {log.email_type?.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-900">{log.recipient}</td>
                  <td className="px-4 py-3 text-sm text-gray-600 max-w-[250px] truncate" title={log.subject}>
                    {log.subject || '-'}
                  </td>
                  <td className="px-4 py-3">
                    <StatusBadge success={log.status === 'sent'} />
                    {log.error && (
                      <div className="text-xs text-red-600 mt-1 max-w-[200px] truncate" title={log.error}>
                        {log.error}
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page === 1}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Previous
          </button>
          <span className="px-3 py-1.5 text-sm text-gray-600">
            Page {page} of {totalPages}
          </span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="px-3 py-1.5 text-sm border border-gray-200 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
          >
            Next
          </button>
        </div>
      )}
    </div>
  );
};

// Main Page
const AdminLogFiles = () => {
  const [activeTab, setActiveTab] = useState('uploads');
  const [overallStats, setOverallStats] = useState(null);

  useEffect(() => {
    const fetchOverallStats = async () => {
      try {
        const res = await api.get('/admin/logs/stats');
        setOverallStats(res.data);
      } catch (error) {
        console.error('Failed to fetch overall stats');
      }
    };
    fetchOverallStats();
  }, []);

  return (
    <Sidebar>
      <div className="p-8 bg-gray-50/50 min-h-screen">
        {/* Header */}
        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 rounded-xl" style={{ backgroundColor: BRAND_BLUE }}>
            <FileText className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold text-gray-900">Log Files</h1>
            <p className="text-gray-500 text-sm mt-1">Review upload and email activity logs</p>
          </div>
        </div>

        {/* Tabs */}
        <div className="border-b border-gray-200 mb-6">
          <div className="flex gap-6">
            <Tab
              active={activeTab === 'uploads'}
              onClick={() => setActiveTab('uploads')}
              icon={Upload}
              count={overallStats?.uploads?.total}
            >
              Upload Logs
            </Tab>
            <Tab
              active={activeTab === 'emails'}
              onClick={() => setActiveTab('emails')}
              icon={Mail}
              count={overallStats?.emails?.total}
            >
              Email Logs
            </Tab>
          </div>
        </div>

        {/* Tab Content */}
        {activeTab === 'uploads' && <UploadLogsTab />}
        {activeTab === 'emails' && <EmailLogsTab />}
      </div>
    </Sidebar>
  );
};

export default AdminLogFiles;
